/**
 * Скачать ответ, считая проценты по-настоящему.
 *
 * ⚠️ ЗАЧЕМ ОТДЕЛЬНО ОТ ПЛЕЕРА. В общем проигрывателе файл тянет сам браузер, и
 * единственный источник — `buffered`: он говорит о положении в записи, а не о
 * скачанных байтах, и у длинных записей (Range) врёт. Здесь наоборот: запрос
 * наш, `Content-Length` известен, и процент — настоящая доля байтов. Это
 * единственное место, где число можно назвать точным.
 *
 * ⚠️ ПРОЦЕНТ — НЕОБЯЗАТЕЛЬНОЕ УКРАШЕНИЕ, А НЕ УСЛОВИЕ. Если сервер не прислал
 * длину или поток недоступен (старый браузер, подделка в проверках), файл всё
 * равно обязан скачаться — просто молча. Ошибку загрузки решает вызывающий.
 */

/** Ответ, из которого можно собрать файл. Своё описание — чтобы проверки не тащили DOM. */
export type ProgressResponse = {
  headers: { get(name: string): string | null };
  blob(): Promise<Blob>;
  body?: ReadableStream<Uint8Array> | null;
};

export function totalBytes(headers: { get(name: string): string | null }): number | null {
  const raw = headers.get('Content-Length');
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function bytesPercent(loaded: number, total: number | null): number | null {
  if (total == null || total <= 0) return null;
  return Math.min(100, Math.floor((loaded / total) * 100));
}

/**
 * Прочитать тело ответа целиком, сообщая долю скачанного.
 *
 * `onPercent` получает `null`, когда честного числа нет: тогда показывать надо
 * обычное ожидание, а не ноль процентов.
 */
export async function downloadWithProgress(
  res: ProgressResponse,
  onPercent: (percent: number | null) => void,
): Promise<Blob> {
  const total = totalBytes(res.headers);
  const reader = res.body?.getReader?.();
  if (!reader || total == null) {
    // Длины нет или поток недоступен — скачиваем как раньше, без чисел.
    onPercent(null);
    return res.blob();
  }
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  onPercent(0);
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;
    chunks.push(value);
    loaded += value.byteLength;
    onPercent(bytesPercent(loaded, total));
  }
  const type = res.headers.get('Content-Type') ?? '';
  return new Blob(chunks as BlobPart[], type ? { type } : undefined);
}
